import { Card, CircularProgress, Stack, type SxProps, type Theme, Typography } from '@mui/material'
import { BlueButton } from '../../../../../customComponents/buttons/BlueButton'
import { IosShare } from '@mui/icons-material'
import { useEffect, useState } from 'react'
import generatePDF, { Margin } from 'react-to-pdf'
import { GetSurvey } from '../../../../../../services/SurveyService'
import { useLocation, useParams } from 'react-router-dom'
import { SurveyType } from '../../../../../../models/SurveySchema'
import { handleApiResponse } from '../../../../../../utils/apiHelpers'
import { RCNMidYearSummary } from './RCNMidYearSummary'
import { ISDMidYearSummary } from './ISDMidYearSummary'
import { useProgressReportAccess } from '../../../../../../hooks/use-progressReportAccess'

export interface SurveyElement {
  type: string
  name: string
  title?: string
  text?: string
  variant?: string
  description?: string
  choices?: string[]
  radioMatrix?: Record<string, { hasUpload?: boolean, hasTextInput?: boolean, infoText?: string }> | null
  visibleIf?: string | null
  templateElements?: SurveyElement[]
  conditionalRenderKey?: string | null
}

export interface SurveyPage {
  title: string
  elements: SurveyElement[]
}

export interface SurveyContent {
  title: string
  pages: SurveyPage[]
}

const PDF_ELEMENT_ID = 'midyear-summary-pdf'

const delay = async (ms: number): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, ms))
}

const toggleAccordions = (expand: boolean): void => {
  const accordions = document.getElementById(PDF_ELEMENT_ID)?.querySelectorAll('[data-testid^="accordion-"]')
  accordions?.forEach((accordion) => {
    const accordionElement = accordion as HTMLElement
    const summary = accordionElement.querySelector('.MuiAccordionSummary-root')
    const isExpanded = accordionElement.classList.contains('Mui-expanded')
    if ((summary != null) && ((expand && !isExpanded) || (!expand && isExpanded))) {
      (summary as HTMLButtonElement).click()
    }
  })
}

const styles: Record<string, SxProps<Theme>> = {
  container: {
    backgroundColor: 'var(--BaseTones-primitive-nuetral-50, rgba(238, 238, 239, 1))',
    borderRadius: '12px',
    padding: '20px 40px 40px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    position: 'relative',
    overflow: 'visible'
  },
  section: {
    backgroundColor: 'var(--BaseTones-primitive-surface-100, rgba(255, 255, 255, 1))',
    padding: '32px 40px 40px 40px',
    borderRadius: '8px',
    border: '1px solid var(--Theme-primitive-primary-300, rgba(84, 153, 195, 1))',
    boxShadow: '0px 2px 7px -1px var(--BaseTones-primitive-nuetral-900-a8, rgba(37, 36, 38, 0.08)), 0px 1px 0px 0px var(--BaseTones-primitive-nuetral-900-a8, rgba(37, 36, 38, 0.08))'
  }
}

export const MidYearSummary = (): JSX.Element => {
  const { planId, planOrgId, rcnId } = useParams()
  const location = useLocation()
  const annualPlanId = planId != null ? Number(planId) : 0
  const planOrgIdNum = planOrgId != null ? Number(planOrgId) : 0
  const rcnIdNum = Number(rcnId)

  const { isLoading: isLoadingAccess, surveyType, hasAccess } = useProgressReportAccess({
    annualPlanId,
    planOrgId: planOrgIdNum,
    rcnId: rcnIdNum,
    surveyTypeFromState: location.state?.surveyType,
    redirectPath: '../..'
  })

  const [isLoading, setIsLoading] = useState(true)
  const [content, setContent] = useState<SurveyContent | null>(null)
  const [answers, setAnswers] = useState<Record<string, any>>({})

  const exportPDF = async (): Promise<void> => {
    try {
      toggleAccordions(true)
      await delay(1000)
      await generatePDF(() => document.getElementById(PDF_ELEMENT_ID), { method: 'open', page: { margin: Margin.MEDIUM, format: 'letter', orientation: 'portrait' } })
    } finally {
      toggleAccordions(false)
    }
  }

  useEffect(() => {
    const loadSummary = async (): Promise<void> => {
      if (!hasAccess || surveyType == null) return

      const summary = await handleApiResponse(GetSurvey(surveyType, planOrgIdNum))
      if (summary != null) {
        setContent(JSON.parse(summary.content))
        if (summary.answers != null) {
          setAnswers(JSON.parse(summary.answers))
        }
      }
      setIsLoading(false)
    }

    void loadSummary()
  }, [hasAccess, surveyType, annualPlanId, planOrgIdNum])

  if (isLoadingAccess || isLoading || surveyType == null) {
    return <div><CircularProgress /></div>
  }

  if (content == null) return <div>No MidYear Summary found</div>

  return (
    <Card sx={styles.container} data-testid='midYearSummaryCard'>
      <Stack gap='20px'>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography variant='h1'>Mid-Year Progress Report Submission</Typography>
          <BlueButton onClick={exportPDF} endIcon={<IosShare />}>Export</BlueButton>
        </Stack>

        <div id='midyear-summary-pdf'>
          <Stack sx={styles.section} gap={2}>
            {surveyType === SurveyType.RCNMidYear
              ? <RCNMidYearSummary content={content} answers={answers} />
              : <ISDMidYearSummary content={content} answers={answers} />}
          </Stack>
        </div>
      </Stack>
    </Card>
  )
}
